import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, KeyRound, Sparkles } from "lucide-react";
import { BoursicoteLogo } from "@/components/BoursicoteLogo";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { apiBase, setAuthToken } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";

type Step = "email" | "otp";

export default function AuthPage() {
  useSeo({ title: "Connexion", canonicalPath: "/login", noindex: true });
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [devOtpHint, setDevOtpHint] = useState<string | null>(null);
  const navigate = useNavigate();

  // Tries to pull the latest OTP from the dev endpoint (only enabled when
  // Resend is not configured server-side). Lets the user skip checking logs.
  async function fetchDevOtp() {
    try {
      const res = await fetch(
        `${apiBase}/api/auth/dev/last-otp?email=${encodeURIComponent(email)}`,
        { credentials: "include" }
      );
      if (!res.ok) return null;
      const data = (await res.json()) as { code: string | null };
      return data.code;
    } catch {
      return null;
    }
  }

  // Email OTP flow — Better Auth endpoints :
  //   POST /api/auth/email-otp/send-verification-otp   (body: {email, type})
  //   POST /api/auth/sign-in/email-otp                 (body: {email, otp}) → sets session cookie
  async function sendOtp(e: React.FormEvent) {
    e.preventDefault();
    if (!email.includes("@")) return toast.error("Email invalide");
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/auth/email-otp/send-verification-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, type: "sign-in" }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setStep("otp");
      // In dev (no Resend), auto-fill the code so we don't grep logs.
      const code = await fetchDevOtp();
      if (code) {
        setOtp(code);
        setDevOtpHint(code);
        toast.success(`Code auto-rempli (mode dev) : ${code}`);
      } else {
        toast.success("Code envoyé par email");
      }
    } catch (e) {
      toast.error("Erreur d'envoi du code");
    } finally {
      setLoading(false);
    }
  }

  async function verifyOtp(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/auth/sign-in/email-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, otp }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      // Better Auth returns { token, user }. Stash the token so subsequent
      // API calls can authenticate via Authorization: Bearer even if the
      // session cookie was blocked by the browser.
      const data = (await res.json()) as { token?: string; user?: any };
      if (data.token) {
        setAuthToken(data.token);
      } else {
        console.warn("[Auth] sign-in returned no token");
      }
      toast.success("Connecté");
      // Hard reload so React Query refetches `me` and the AppLayout updates.
      // Land on /onboarding ; it self-redirects to /forums when already done.
      window.location.assign("/onboarding");
    } catch (e) {
      toast.error("Code invalide");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center bg-page p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-xl mb-2">
            <BoursicoteLogo variant="wordmark" />
          </CardTitle>
          <p className="text-sm text-foreground-muted">
            {step === "email"
              ? "Connectez-vous par email pour suivre vos forums et utilisateurs Boursorama."
              : "Saisissez le code reçu par email."}
          </p>
        </CardHeader>
        <CardContent>
          {step === "email" ? (
            <form onSubmit={sendOtp} className="flex flex-col gap-3">
              <label className="text-sm font-medium" htmlFor="email">Email</label>
              <div className="relative">
                <Mail className="absolute left-2 top-1/2 -translate-y-1/2 size-4 text-foreground-muted" />
                <Input
                  id="email"
                  type="email"
                  className="pl-8"
                  placeholder="toi@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" disabled={loading} className="mt-2">
                {loading ? "Envoi…" : "Recevoir un code"}
              </Button>
            </form>
          ) : (
            <form onSubmit={verifyOtp} className="flex flex-col gap-3">
              <label className="text-sm font-medium" htmlFor="otp">Code à 6 chiffres</label>
              <div className="relative">
                <KeyRound className="absolute left-2 top-1/2 -translate-y-1/2 size-4 text-foreground-muted" />
                <Input
                  id="otp"
                  className="pl-8 tracking-[0.4em] text-center font-mono"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  placeholder="••••••"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  required
                />
              </div>
              {devOtpHint && (
                <div className="flex items-center gap-2 rounded-md bg-primary/10 text-primary text-xs px-2 py-1.5">
                  <Sparkles className="size-3.5" />
                  <span>Mode dev : code récupéré automatiquement ({devOtpHint})</span>
                </div>
              )}
              <Button type="submit" disabled={loading}>
                {loading ? "Vérification…" : "Se connecter"}
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => setStep("email")}>
                Changer d'email
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
